import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageHeader } from "@/components/PageHeader";
import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { DataTable } from "@/components/DataTable";
import {
  api,
  ApiError,
  type AppliedExperimentEntry,
  type AppliedExperiments,
  type ExperimentRow,
} from "@/lib/api";
import { formatDate, relativeIso } from "@/lib/format";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Check, ChevronsUpDown, FlaskConical, Pin, PinOff, Plus, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

const EXPERIMENTS_QUERY = ["admin-experiments"] as const;
// The server enforces this prefix on every write; here it's only display
// (fixed adornment in the create dialog).
const PREFIX = "brain-exp-";

export function AdminExperimentsPage() {
  useDocumentTitle("Admin: Experiments");
  const listQuery = useQuery({
    queryKey: EXPERIMENTS_QUERY,
    queryFn: api.admin.experiments.list,
    retry: (count, err) =>
      !(err instanceof ApiError && err.code === "experiments_unconfigured") && count < 2,
  });

  const [search, setSearch] = useState("");
  // "" = no brain picked → no Resolved column.
  const [brainId, setBrainId] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [prefill, setPrefill] = useState<{ suffix: string; description: string } | null>(null);
  const [editTarget, setEditTarget] = useState<ExperimentRow | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<ExperimentRow | null>(null);

  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: EXPERIMENTS_QUERY });

  const infraQuery = useQuery({
    queryKey: ["admin-infrastructure"],
    queryFn: api.admin.infrastructure,
  });
  const brains = useMemo<BrainOption[]>(() => {
    const fleet = (infraQuery.data?.containers ?? [])
      .filter((c) => c.agentType === "boo-brain")
      .map((c) => ({ id: c.id, label: `${c.name} — ${c.organizationName}`, pool: false }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const pool = (infraQuery.data?.poolContainers ?? [])
      .filter((c) => c.agentType === "boo-brain")
      .map((c) => ({ id: c.id, label: c.name, pool: true }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [...fleet, ...pool];
  }, [infraQuery.data]);
  const brainLabel = useMemo(() => {
    const m = new Map(brains.map((b) => [b.id, b.label]));
    return (id: string) => m.get(id) ?? id;
  }, [brains]);

  const resolution = useQuery({
    queryKey: ["admin-experiment-resolution", brainId],
    queryFn: () => api.admin.experiments.resolution(brainId),
    enabled: brainId !== "",
  });

  // Ground truth from the deployment itself (its /internal/experiments
  // route) — what the brain is running, vs. what PostHog would serve it.
  const applied = useQuery({
    queryKey: ["admin-experiment-applied", brainId],
    queryFn: () => api.admin.experiments.applied(brainId),
    enabled: brainId !== "",
    retry: 1,
  });

  const appliedByKey = useMemo(() => {
    const m = new Map<string, AppliedExperimentEntry>();
    for (const e of applied.data?.data.experiments ?? []) m.set(e.flagKey, e);
    return m;
  }, [applied.data]);

  // Experiments the brain's code registers that have no PostHog flag yet —
  // the "create the flag" reminder, prefilled from the brain's registry.
  const unflaggedExperiments = useMemo(() => {
    if (!applied.data) return [];
    const known = new Set((listQuery.data?.data ?? []).map((f) => f.key));
    return applied.data.data.experiments.filter((e) => !known.has(e.flagKey));
  }, [applied.data, listQuery.data]);

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      api.admin.experiments.update(id, { active }),
    onSuccess: (res) => {
      toast.success(`${res.data.key} ${res.data.active ? "enabled" : "disabled"}`);
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["admin-experiment-resolution"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Update failed"),
  });

  // Pin/unpin the selected brain straight from its row — the common
  // "put this one brain in the experiment" gesture.
  const setPins = useMutation({
    mutationFn: ({ id, pinnedContainerIds }: { id: number; pinnedContainerIds: string[] }) =>
      api.admin.experiments.update(id, { pinnedContainerIds }),
    onSuccess: (res) => {
      toast.success(`${res.data.key}: ${res.data.pinnedContainerIds.length} pinned`);
      invalidate();
      queryClient.invalidateQueries({ queryKey: ["admin-experiment-resolution"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Update failed"),
  });

  const archive = useMutation({
    mutationFn: (id: number) => api.admin.experiments.archive(id),
    onSuccess: () => {
      toast.success("Experiment archived");
      invalidate();
      setArchiveTarget(null);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Archive failed"),
  });

  const rows = useMemo(() => {
    const all = listQuery.data?.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (f) => f.key.includes(q) || f.description.toLowerCase().includes(q),
    );
  }, [listQuery.data, search]);

  const columns = useMemo<ColumnDef<ExperimentRow>[]>(() => {
    const base: ColumnDef<ExperimentRow>[] = [
      {
        accessorKey: "key",
        header: "Key",
        cell: ({ row }) => (
          <span className="font-mono text-sm whitespace-nowrap">{row.original.key}</span>
        ),
      },
      {
        accessorKey: "description",
        header: "Description",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground line-clamp-2">
            {row.original.description || "—"}
          </span>
        ),
      },
      {
        id: "rollout",
        header: "Rollout",
        cell: ({ row }) =>
          row.original.rolloutPercentage !== null ? (
            <span className="text-sm tabular-nums">{row.original.rolloutPercentage}%</span>
          ) : (
            // Targeting was customized in PostHog (conditions/variants) —
            // shown but only editable there.
            <Badge variant="outline">Custom targeting</Badge>
          ),
      },
      {
        id: "pinned",
        header: () => <span title="Brains served regardless of rollout">Pinned</span>,
        enableSorting: false,
        cell: ({ row }) => {
          const pins = row.original.pinnedContainerIds;
          const custom = row.original.rolloutPercentage === null;
          const isPinned = brainId !== "" && pins.includes(brainId);
          return (
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <span
                className={`text-sm tabular-nums ${pins.length === 0 ? "text-muted-foreground" : ""}`}
                title={pins.map(brainLabel).join("\n")}
              >
                {pins.length}
              </span>
              {brainId !== "" && !custom && (
                <Button
                  size="sm"
                  variant={isPinned ? "secondary" : "outline"}
                  className="h-6 gap-1 px-2 text-xs"
                  disabled={setPins.isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPins.mutate({
                      id: row.original.id,
                      pinnedContainerIds: isPinned
                        ? pins.filter((id) => id !== brainId)
                        : [...pins, brainId],
                    });
                  }}
                  aria-label={`${isPinned ? "Unpin" : "Pin"} selected brain for ${
                    row.original.key
                  }`}
                >
                  {isPinned ? <PinOff className="size-3" /> : <Pin className="size-3" />}
                  {isPinned ? "Unpin" : "Pin"}
                </Button>
              )}
            </span>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {formatDate(row.original.createdAt)}
          </span>
        ),
      },
      {
        id: "active",
        header: "Active",
        enableSorting: false,
        cell: ({ row }) => (
          <Switch
            checked={row.original.active}
            disabled={toggleActive.isPending}
            onCheckedChange={(on) => toggleActive.mutate({ id: row.original.id, active: on })}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Toggle ${row.original.key}`}
          />
        ),
      },
    ];

    if (brainId !== "") {
      base.push(
        {
          id: "resolved",
          header: () => <span title="What PostHog serves this brain">Resolved</span>,
          enableSorting: false,
          cell: ({ row }) => {
            if (resolution.isLoading) return <Skeleton className="h-5 w-10 rounded-md" />;
            const value = resolution.data?.data.flags[row.original.key];
            if (value === undefined)
              // PostHog returns nothing for this key (flag inactive/archived)
              // — distinct from an explicit false: the brain then falls back
              // to its built-in default, which may be On.
              return (
                <span
                  className="text-xs text-muted-foreground"
                  title="PostHog returns nothing — the brain uses its built-in default"
                >
                  not served
                </span>
              );
            return <ValueBadge value={value} />;
          },
        },
        {
          id: "applied",
          header: () => (
            <span title="What the brain is running right now (polls every minute)">Applied</span>
          ),
          enableSorting: false,
          cell: ({ row }) => {
            if (applied.isLoading) return <Skeleton className="h-5 w-10 rounded-md" />;
            if (applied.isError) return <span className="text-sm text-muted-foreground">—</span>;
            const entry = appliedByKey.get(row.original.key);
            if (!entry)
              // Flag exists in PostHog but this brain's code has no such
              // experiment registered — it can never react to it.
              return <span className="text-xs text-muted-foreground">not registered</span>;
            return (
              <span className="flex items-center gap-1.5">
                <ValueBadge value={entry.value} />
                {entry.source === "default" && (
                  <span className="text-xs text-muted-foreground">default</span>
                )}
              </span>
            );
          },
        },
      );
    }

    base.push({
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="ghost"
          className="text-destructive hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation();
            setArchiveTarget(row.original);
          }}
        >
          Archive
        </Button>
      ),
    });

    return base;
  }, [
    brainId,
    resolution.isLoading,
    resolution.data,
    applied.isLoading,
    applied.isError,
    appliedByKey,
    toggleActive,
    setPins,
    brainLabel,
  ]);

  const unconfigured =
    listQuery.error instanceof ApiError && listQuery.error.code === "experiments_unconfigured";

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title="Experiments"
          subtitle="Runtime flags for the brain fleet, served by PostHog."
        />
        {!unconfigured && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            New experiment
          </Button>
        )}
      </div>

      {listQuery.isLoading ? (
        <LoadingIndicator />
      ) : unconfigured ? (
        <div className="flex items-center gap-3 rounded-md border p-4 text-sm text-muted-foreground">
          <FlaskConical className="size-4 shrink-0" />
          <span>
            Set <code>BRAIN_FF_POSTHOG_API_KEY</code> and <code>BRAIN_FF_POSTHOG_PROJECT_ID</code>{" "}
            on boo-backend to enable experiments.
          </span>
        </div>
      ) : listQuery.isError || !listQuery.data ? (
        <ErrorComponent error={listQuery.error ?? "Failed to load experiments"} />
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search key or description…"
                className="pl-8"
              />
            </div>
            <BrainCombobox
              brains={brains}
              loading={infraQuery.isLoading}
              value={brainId}
              onSelect={setBrainId}
            />
            {brainId !== "" && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setBrainId("")}
                aria-label="Clear brain selection"
              >
                <X className="size-4" />
                Clear
              </Button>
            )}
          </div>

          {brainId !== "" && resolution.isError && (
            <p className="text-sm text-destructive">
              {resolution.error instanceof Error
                ? resolution.error.message
                : "Failed to resolve experiments for this brain"}
            </p>
          )}

          {brainId !== "" && unflaggedExperiments.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
              <span>
                {unflaggedExperiments.length} brain experiment
                {unflaggedExperiments.length === 1 ? "" : "s"} without a flag:
              </span>
              {unflaggedExperiments.map((e) => (
                <Button
                  key={e.flagKey}
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1 font-mono text-xs"
                  onClick={() => {
                    setPrefill({
                      suffix: e.flagKey.startsWith(PREFIX)
                        ? e.flagKey.slice(PREFIX.length)
                        : e.flagKey,
                      description: e.summary,
                    });
                    setCreateOpen(true);
                  }}
                >
                  {e.flagKey}
                  <Plus className="size-3" />
                </Button>
              ))}
            </div>
          )}

          {rows.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {search.trim() ? "No matches." : "No experiments yet."}
            </p>
          ) : (
            <DataTable
              columns={columns}
              data={rows}
              enableSorting
              initialSorting={[{ id: "key", desc: false }]}
              onRowClick={setEditTarget}
            />
          )}

          {brainId !== "" && <AppliedStatusLine applied={applied} />}
        </div>
      )}

      <CreateExperimentDialog
        key={prefill ? `prefill-${prefill.suffix}` : "blank"}
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) setPrefill(null);
        }}
        initialSuffix={prefill?.suffix ?? ""}
        initialDescription={prefill?.description ?? ""}
        onCreated={() => {
          setCreateOpen(false);
          setPrefill(null);
          invalidate();
        }}
      />

      <EditExperimentSheet
        key={editTarget?.id ?? "none"}
        experiment={editTarget}
        brains={brains}
        onClose={() => setEditTarget(null)}
        onSaved={() => {
          invalidate();
          queryClient.invalidateQueries({ queryKey: ["admin-experiment-resolution"] });
        }}
      />

      <ConfirmDialog
        open={archiveTarget !== null}
        onOpenChange={(open) => !open && setArchiveTarget(null)}
        title="Archive experiment?"
        description={
          archiveTarget
            ? `${archiveTarget.key} stops being served — brains fall back to their built-in default.`
            : ""
        }
        confirmLabel="Archive"
        variant="destructive"
        loading={archive.isPending}
        onConfirm={() => archiveTarget && archive.mutate(archiveTarget.id)}
      />
    </div>
  );
}

function ValueBadge({ value }: { value: boolean | string }) {
  if (value === true) return <Badge>On</Badge>;
  if (value === false) return <Badge variant="secondary">Off</Badge>;
  return (
    <Badge variant="outline" className="font-mono">
      {String(value)}
    </Badge>
  );
}

// One line under the table summarizing how trustworthy the two per-brain
// columns are right now.
function AppliedStatusLine({
  applied,
}: {
  applied: UseQueryResult<{ data: AppliedExperiments }, Error>;
}) {
  const ph = applied.data?.data.posthog;
  let text: string;
  let error = true;
  if (applied.isLoading) return null;
  if (applied.isError) {
    text = "Brain unreachable — PostHog values only.";
  } else if (ph && !ph.configured) {
    text = "No PostHog key on this brain — defaults only.";
  } else if (ph?.lastError) {
    text = `PostHog unreachable from this brain${
      ph.lastPollAt ? ` since ${relativeIso(new Date(ph.lastPollAt).toISOString())}` : ""
    } — applied values are last-known.`;
  } else if (ph?.lastPollAt) {
    error = false;
    text = `Last poll ${relativeIso(new Date(ph.lastPollAt).toISOString())}`;
  } else {
    return null;
  }
  return <p className={`text-xs ${error ? "text-destructive" : "text-muted-foreground"}`}>{text}</p>;
}

type BrainOption = { id: string; label: string; pool: boolean };

// Searchable brain picker — a plain Select stops scaling past a few dozen
// brains. All brains are loaded client-side (one infrastructure call) and
// cmdk filters as you type; the list itself scrolls.
function BrainCombobox({
  brains,
  loading,
  value,
  onSelect,
  placeholder = "Resolve for a brain…",
  className = "w-80",
}: {
  brains: BrainOption[];
  loading: boolean;
  value: string;
  onSelect: (id: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = brains.find((b) => b.id === value);
  const fleet = brains.filter((b) => !b.pool);
  const pool = brains.filter((b) => b.pool);

  const renderItem = (b: BrainOption) => (
    <CommandItem
      key={b.id}
      // The searchable text: label + id so pasting a container id works.
      value={`${b.label} ${b.id}`}
      onSelect={() => {
        onSelect(b.id);
        setOpen(false);
      }}
    >
      <Check className={value === b.id ? "opacity-100" : "opacity-0"} />
      <span className="truncate">{b.label}</span>
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`${className} justify-between font-normal`}
        >
          <span className={`truncate ${selected ? "" : "text-muted-foreground"}`}>
            {selected ? selected.label : loading ? "Loading brains…" : placeholder}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={`${className} p-0`} align="start">
        <Command>
          <CommandInput placeholder={`Search ${brains.length} brains…`} />
          <CommandList>
            <CommandEmpty>No brains match.</CommandEmpty>
            {fleet.length > 0 && <CommandGroup heading="Fleet">{fleet.map(renderItem)}</CommandGroup>}
            {pool.length > 0 && (
              <CommandGroup heading="Warm pool">{pool.map(renderItem)}</CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function CreateExperimentDialog({
  open,
  onOpenChange,
  onCreated,
  initialSuffix = "",
  initialDescription = "",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
  // Prefill from a brain-registered experiment that has no flag yet. The
  // parent remounts this dialog (React `key`) when the prefill changes.
  initialSuffix?: string;
  initialDescription?: string;
}) {
  const [suffix, setSuffix] = useState(initialSuffix);
  const [description, setDescription] = useState(initialDescription);
  const [rollout, setRollout] = useState(100);
  const [active, setActive] = useState(false);

  const reset = () => {
    setSuffix("");
    setDescription("");
    setRollout(100);
    setActive(false);
  };

  const create = useMutation({
    mutationFn: () =>
      api.admin.experiments.create({
        key: `${PREFIX}${suffix.trim()}`,
        description: description.trim(),
        active,
        rolloutPercentage: rollout,
      }),
    onSuccess: (res) => {
      toast.success(`${res.data.key} created`);
      reset();
      onCreated();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Create failed"),
  });

  const validSuffix = /^[a-z0-9]([a-z0-9_-]*[a-z0-9])?$/.test(suffix.trim());

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New experiment</DialogTitle>
          <DialogDescription>The key must match what the brain code reads.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="exp-key">Key</Label>
            <div className="flex">
              <span className="flex h-8 shrink-0 items-center whitespace-nowrap rounded-l-lg border border-r-0 border-input bg-muted px-2.5 font-mono text-sm text-muted-foreground">
                {PREFIX}
              </span>
              <Input
                id="exp-key"
                className="rounded-l-none font-mono"
                placeholder="planner-v2"
                value={suffix}
                onChange={(e) => setSuffix(e.target.value.toLowerCase())}
              />
            </div>
            {suffix.trim() !== "" && !validSuffix && (
              <p className="text-xs text-destructive">
                Lowercase letters, digits, hyphens or underscores only.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="exp-desc">Description</Label>
            <Textarea
              id="exp-desc"
              placeholder="What this experiment changes"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="exp-rollout">Rollout</Label>
            <div className="relative w-28">
              <Input
                id="exp-rollout"
                type="number"
                min={0}
                max={100}
                className="pr-7 tabular-nums"
                value={rollout}
                onChange={(e) =>
                  setRollout(Math.max(0, Math.min(100, Math.round(Number(e.target.value) || 0))))
                }
              />
              <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                %
              </span>
            </div>
          </div>

          <label className="flex items-center justify-between gap-4 rounded-lg border px-3 py-2.5 cursor-pointer">
            <span className="text-sm font-medium">Active immediately</span>
            <Switch checked={active} onCheckedChange={setActive} aria-label="Active immediately" />
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={!validSuffix || create.isPending}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditExperimentSheet({
  experiment,
  brains,
  onClose,
  onSaved,
}: {
  experiment: ExperimentRow | null;
  brains: BrainOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  // Remounted per experiment via a React `key` on the call site, so these
  // initializers run fresh for each row — no reset effect needed.
  const [description, setDescription] = useState(experiment?.description ?? "");
  const [rollout, setRollout] = useState(experiment?.rolloutPercentage ?? 100);
  const [pinned, setPinned] = useState<string[]>(experiment?.pinnedContainerIds ?? []);
  const [active, setActive] = useState(experiment?.active ?? false);

  const customTargeting = experiment !== null && experiment.rolloutPercentage === null;
  const brainLabel = (id: string) => brains.find((b) => b.id === id)?.label ?? id;
  const unpinnedBrains = brains.filter((b) => !pinned.includes(b.id));

  const save = useMutation({
    mutationFn: () =>
      api.admin.experiments.update(experiment!.id, {
        description: description.trim(),
        active,
        ...(customTargeting ? {} : { rolloutPercentage: rollout, pinnedContainerIds: pinned }),
      }),
    onSuccess: () => {
      toast.success("Experiment updated");
      onSaved();
      onClose();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Update failed"),
  });

  if (!experiment) return null;

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full gap-0 sm:max-w-md">
        <SheetHeader className="border-b">
          <SheetTitle className="font-mono text-base">{experiment.key}</SheetTitle>
          <SheetDescription>
            Created {formatDate(experiment.createdAt)}
            {experiment.createdBy ? ` by ${experiment.createdBy}` : ""}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-desc">Description</Label>
            <Textarea
              id="edit-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-rollout">Rollout</Label>
            {customTargeting ? (
              <p className="text-sm text-muted-foreground">
                Custom targeting — edit rollout and pins in PostHog.
              </p>
            ) : (
              <div className="relative w-28">
                <Input
                  id="edit-rollout"
                  type="number"
                  min={0}
                  max={100}
                  className="pr-7 tabular-nums"
                  value={rollout}
                  onChange={(e) =>
                    setRollout(Math.max(0, Math.min(100, Math.round(Number(e.target.value) || 0))))
                  }
                />
                <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  %
                </span>
              </div>
            )}
          </div>

          {!customTargeting && (
            <div className="space-y-1.5">
              <Label>Pinned brains</Label>
              <p className="text-xs text-muted-foreground">
                Always served the experiment, whatever the rollout.
              </p>
              {pinned.length > 0 && (
                <ul className="divide-y rounded-lg border">
                  {pinned.map((id) => (
                    <li key={id} className="flex items-center justify-between gap-2 px-3 py-1.5">
                      <span className="truncate text-sm" title={id}>
                        {brainLabel(id)}
                      </span>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-6 shrink-0"
                        onClick={() => setPinned(pinned.filter((p) => p !== id))}
                        aria-label={`Unpin ${brainLabel(id)}`}
                      >
                        <X className="size-3.5" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              <BrainCombobox
                brains={unpinnedBrains}
                loading={false}
                value=""
                onSelect={(id) => setPinned([...pinned, id])}
                placeholder="Pin a brain…"
                className="w-full"
              />
            </div>
          )}

          <label className="flex items-center justify-between gap-4 rounded-lg border px-3 py-2.5 cursor-pointer">
            <span className="text-sm font-medium">Active</span>
            <Switch checked={active} onCheckedChange={setActive} aria-label="Active" />
          </label>
        </div>

        <SheetFooter className="flex-row justify-end gap-2 border-t">
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            Save
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
